import { useEffect, useMemo, useState } from "react";
import { api, pesanGalat } from "../../lib/api";
import { toast } from "sonner";
import {
  X, ShieldAlert, Search, History, RotateCcw, Loader2, AlertTriangle,
  ShieldCheck, Info, ChevronDown,
} from "lucide-react";
import { useModalGuard, useModalLayer, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useDebounce } from "../../hooks/useDebounce";

/**
 * Panel "Atur Modal & Margin" — brief owner 18 Sep 2026 (batch P, c7).
 *
 * SATU ATURAN KERAS di seluruh berkas ini: layar TIDAK PERNAH menghitung
 * ulang kebijakan (siapa boleh lihat apa) — semua angka "Hasil", ringkasan,
 * dan daftar dampak datang dari `GET/POST /inventory/price-list/akses-modal*`
 * (lihat `routers/inventory/akses_modal_pengaturan.py`). Layar ini hanya
 * mengumpulkan PILIHAN (draf) dan menampilkan APA KATA SERVER tentang
 * pilihan itu — persis pola "server yang menghitung dampak" di §8
 * `memory/DESAIN-BRIEF-18-SEP.md`.
 *
 * Gerbang tombol pembuka panel ada di `PriceList.jsx` (`user?.role ===
 * "superadmin"`), TAPI panel ini sendiri tidak pernah dirender untuk role
 * lain karena satu-satunya jalan membukanya adalah tombol itu — endpoint di
 * baliknya (`require_role("superadmin")`) tetap menolak siapa pun selain
 * Bos apa pun yang terjadi di layar (aturan 2/3 CLAUDE.md: gerbang backend
 * yang sesungguhnya, UI cuma tidak menawarkan tombol yang berujung 403).
 */

const TINGKAT_LABEL = {
  lengkap: "Modal & margin", kelas: "Kelas margin", tidak: "Tidak melihat",
};
const HASIL_TEKS = {
  lengkap: "Modal & margin terlihat",
  kelas: "Kelas margin saja (Rugi/Tipis/Normal/Tebal, tanpa angka)",
  tidak: "Tidak melihat modal & margin",
};
const SEGMEN = [
  { v: "ikut", l: "Ikuti dasar" },
  { v: "lengkap", l: "Modal & margin" },
  { v: "kelas", l: "Kelas" },
  { v: "tidak", l: "Tidak" },
];
const JANGKAUAN_LABEL = [
  { k: "price_list", l: "Price List" },
  { k: "penjualan", l: "Penjualan" },
  { k: "setujui_nota", l: "Setujui nota" },
  { k: "retur", l: "Retur" },
];

function draftKeAPI(draft) {
  return Object.entries(draft).map(([user_id, tingkat]) => ({ user_id, tingkat }));
}

function petaAwalDariAkun(akun) {
  const m = {};
  for (const a of akun || []) {
    if (!a.terkunci && a.pengecualian) m[a.id] = a.pengecualian;
  }
  return m;
}

export default function PengaturanAksesModal({ onClose, onTersimpan }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});
  const [q, setQ] = useState("");
  const [chip, setChip] = useState("semua"); // semua | diatur | melihat
  const [showLain, setShowLain] = useState(false);

  const [pratinjau, setPratinjau] = useState(null);
  const [pratinjauLoading, setPratinjauLoading] = useState(false);
  const [pratinjauGagal, setPratinjauGagal] = useState(false);

  const [konfirmasiSimpan, setKonfirmasiSimpan] = useState(false);
  const [konfirmasiKembalikan, setKonfirmasiKembalikan] = useState(false);
  const [busy, setBusy] = useState(false);

  const [riwayat, setRiwayat] = useState([]);
  const [riwayatHabis, setRiwayatHabis] = useState(false);
  const [riwayatLoading, setRiwayatLoading] = useState(false);

  const muat = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/price-list/akses-modal");
      setData(r.data);
      setRiwayat(r.data.riwayat || []);
      setRiwayatHabis((r.data.riwayat || []).length < 20);
      setDraft(petaAwalDariAkun(r.data.akun));
      setPratinjau(null);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memuat pengaturan akses modal & margin"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { muat(); }, []);

  const original = useMemo(() => petaAwalDariAkun(data?.akun), [data]);
  const adaPerubahan = useMemo(() => {
    const semua = new Set([...Object.keys(original), ...Object.keys(draft)]);
    for (const id of semua) if ((original[id] || null) !== (draft[id] || null)) return true;
    return false;
  }, [original, draft]);

  // Draf -> pratinjau server, didebounce ~400ms (§ LAYAR B.5 DESAIN-BRIEF).
  const draftDebounced = useDebounce(useMemo(() => JSON.stringify(draft), [draft]), 400);
  useEffect(() => {
    if (!data) return;
    let batal = false;
    (async () => {
      setPratinjauLoading(true);
      setPratinjauGagal(false);
      try {
        const body = { versi: data.versi, pengecualian: draftKeAPI(draft) };
        const r = await api.post("/inventory/price-list/akses-modal/pratinjau", body);
        if (!batal) setPratinjau(r.data);
      } catch (e) {
        if (!batal) { setPratinjau(null); setPratinjauGagal(true); }
      } finally {
        if (!batal) setPratinjauLoading(false);
      }
    })();
    return () => { batal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftDebounced, data?.versi]);

  const hasilDraf = useMemo(() => {
    const m = {};
    for (const a of pratinjau?.akun || []) m[a.id] = a.tingkat_draf;
    return m;
  }, [pratinjau]);

  const pilihTingkat = (id, v) => {
    setDraft((d) => {
      const next = { ...d };
      if (v === "ikut") delete next[id];
      else next[id] = v;
      return next;
    });
  };

  const cocokCari = (a) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return [a.nama, a.email, a.jabatan, a.cabang].some((v) => (v || "").toLowerCase().includes(s));
  };
  const cocokChip = (a) => {
    if (chip === "diatur") return !!draft[a.id];
    if (chip === "melihat") return (hasilDraf[a.id] ?? a.hasil.tingkat) === "lengkap";
    return true;
  };

  const semuaAkun = data?.akun || [];
  const utama = semuaAkun.filter((a) => a.terjangkau).filter(cocokCari).filter(cocokChip);
  const lain = semuaAkun.filter((a) => !a.terjangkau).filter(cocokCari).filter(cocokChip);

  const bukaKonfirmasiSimpan = () => {
    if (!adaPerubahan) return;
    if (pratinjauLoading || !pratinjau) {
      toast.info("Menunggu pratinjau dampak selesai dihitung server — coba lagi sesaat lagi.");
      return;
    }
    setKonfirmasiSimpan(true);
  };

  const simpan = async () => {
    setBusy(true);
    try {
      const body = { versi: data.versi, pengecualian: draftKeAPI(draft) };
      const r = await api.put("/inventory/price-list/akses-modal", body);
      toast.success("Pengaturan akses modal & margin disimpan");
      setData(r.data);
      setDraft(petaAwalDariAkun(r.data.akun));
      setPratinjau(null);
      setKonfirmasiSimpan(false);
      setRiwayat(r.data.riwayat || []);
      setRiwayatHabis((r.data.riwayat || []).length < 20);
      onTersimpan?.();
    } catch (e) {
      setKonfirmasiSimpan(false);
      if (e?.response?.status === 409) {
        toast.error(pesanGalat(e, "Pengaturan sudah diubah di tab/perangkat lain"));
        await muat();
        toast.info("Panel dimuat ulang — perubahan yang belum disimpan dibuang.");
      } else {
        toast.error(pesanGalat(e, "Gagal menyimpan pengaturan akses modal"));
      }
    } finally {
      setBusy(false);
    }
  };

  const kembalikanSemua = async () => {
    setBusy(true);
    try {
      const r = await api.put("/inventory/price-list/akses-modal",
        { versi: data.versi, pengecualian: [] });
      toast.success("Semua akun dikembalikan ke aturan dasar");
      setData(r.data);
      setDraft({});
      setPratinjau(null);
      setKonfirmasiKembalikan(false);
      setRiwayat(r.data.riwayat || []);
      setRiwayatHabis((r.data.riwayat || []).length < 20);
      onTersimpan?.();
    } catch (e) {
      setKonfirmasiKembalikan(false);
      if (e?.response?.status === 409) {
        toast.error(pesanGalat(e, "Pengaturan sudah diubah di tab/perangkat lain"));
        await muat();
      } else {
        toast.error(pesanGalat(e, "Gagal mengembalikan pengaturan"));
      }
    } finally {
      setBusy(false);
    }
  };

  const muatRiwayatLebih = async () => {
    setRiwayatLoading(true);
    try {
      const r = await api.get("/inventory/price-list/akses-modal/riwayat",
        { params: { lewati: riwayat.length, batas: 20 } });
      const baru = r.data.items || [];
      setRiwayat((rw) => [...rw, ...baru]);
      if (riwayat.length + baru.length >= (r.data.total || 0)) setRiwayatHabis(true);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memuat riwayat"));
    } finally {
      setRiwayatLoading(false);
    }
  };

  const dialogAktif = konfirmasiSimpan || konfirmasiKembalikan;
  const guard = useModalGuard({ onClose, onSubmit: bukaKonfirmasiSimpan, busy, dangerous: true });
  useModalLayer(dialogAktif);

  const akunYangDiatur = Object.keys(original).length;

  return (
    <div className="fixed inset-0 z-50" data-testid="aks-panel-root">
      <div className="fixed inset-0 bg-black/50" onClick={guard.requestClose} />
      <div ref={guard.contentRef} onClick={(e) => e.stopPropagation()}
        data-testid="aks-panel"
        className="fixed inset-y-0 right-0 h-full w-full sm:max-w-[760px] bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <h2 className="font-display font-bold text-base text-slate-900">Atur Modal &amp; Margin</h2>
          </div>
          <button type="button" onClick={guard.requestClose} data-testid="aks-tutup"
            className="w-9 h-9 rounded-lg hover:bg-slate-100 grid place-items-center text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="py-16 text-center text-slate-400 text-sm" data-testid="aks-loading">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Memuat pengaturan…
            </div>
          ) : !data ? (
            <p className="text-sm text-rose-600">Gagal memuat pengaturan.</p>
          ) : (
            <>
              {/* Spanduk jujur — TIDAK bisa ditutup (§ LAYAR B.1). */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2 text-xs text-slate-600"
                data-testid="aks-spanduk">
                <p><b>Mengatur:</b> Price List (layar &amp; Excel), Master Produk (termasuk harga massal),
                  Paket Bundling (termasuk peringatan harga paket di bawah modal), pencarian
                  barcode/produk, serta data penjualan &amp; retur PENJUALAN
                  (termasuk cetak faktur dan persetujuan nota).</p>
                <p><b>TIDAK mengatur:</b> Laporan Stok/Kartu Stok, daftar &amp; riwayat unit IMEI, retur
                  PEMBELIAN, menu Finance (Persediaan, Laporan, Audit), PO &amp; penerimaan, opname,
                  akuisisi, log aktivitas, Asisten AI. Akun Admin &amp; Finance tetap melihat modal di sana
                  lewat role-nya. Kolom "Masih melihat modal di" pada tiap akun menunjukkan jalur yang kami
                  ketahui; daftarnya tidak dijamin lengkap.</p>
                <p>Margin dalam persen + harga jual = modal (modal = harga jual × (1 − margin)). Karena
                  itu tingkat "Kelas margin" tidak menampilkan angka apa pun.
                  {data.ambang_kelas && (
                    <span> Batas kelas: Rugi &lt; 0% · Tipis 0–{data.ambang_kelas.normal}% ·
                      Normal {data.ambang_kelas.normal}–{data.ambang_kelas.tebal}% ·
                      Tebal ≥ {data.ambang_kelas.tebal}%.</span>
                  )}</p>
                <p>Akun Bos (superadmin) selalu melihat semuanya dan tidak bisa diatur di sini.</p>
                <p className="flex items-start gap-1.5 text-amber-700">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Beberapa peringatan tetap bisa dipakai menebak modal lewat percobaan berulang:
                  peringatan "dijual di bawah modal" saat membuat/mengedit nota, jumlah "di bawah modal"
                  saat menyimpan harga massal, dan kelas margin bagi akun yang bisa mengubah harga jual.
                  Setiap percobaan mengubah data sungguhan dan tercatat. Kolom "Bisa ditebak lewat"
                  menandai akun yang punya jalan ini.
                </p>
              </div>

              {(data.rusak || data.gagal) && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-700 flex items-start gap-2"
                  data-testid="aks-rusak">
                  <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                  Pengaturan tidak terbaca/rusak — semua akun selain Bos sementara tidak melihat modal.
                  Simpan ulang untuk memperbaiki.
                </div>
              )}

              {/* Ringkasan — dari pratinjau kalau sedang ada draf, kalau tidak dari GET awal. */}
              <div className="text-sm text-slate-700 bg-white border border-slate-200 rounded-xl px-3 py-2"
                data-testid="aks-ringkasan">
                {(() => {
                  const r = pratinjau?.ringkasan || data.ringkasan;
                  return (
                    <>
                      <b>{r.lengkap}</b> melihat modal &amp; margin · <b>{r.kelas}</b> kelas margin ·
                      {" "}<b>{r.tidak}</b> tidak melihat
                      {akunYangDiatur > 0 && (
                        <span className="text-slate-400"> · {akunYangDiatur} akun diatur khusus tersimpan</span>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Cari & filter */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="aks-cari"
                    placeholder="Cari nama, email, jabatan, atau cabang…"
                    className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                </div>
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs">
                  {[{ v: "semua", l: "Semua" }, { v: "diatur", l: "Diatur khusus" }, { v: "melihat", l: "Melihat modal" }]
                    .map((c) => (
                      <button key={c.v} type="button" onClick={() => setChip(c.v)}
                        data-testid={`aks-filter-${c.v}`}
                        className={`px-3 h-9 font-medium ${chip === c.v ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                        {c.l}
                      </button>
                    ))}
                </div>
              </div>

              {pratinjauGagal && (
                <p className="text-xs text-rose-600" data-testid="aks-galat-pratinjau">
                  Pratinjau gagal — hasil belum dihitung.
                </p>
              )}
              {pratinjau?.basi && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                  data-testid="aks-pratinjau-basi">
                  Pengaturan berubah sejak panel dibuka — muat ulang.
                </p>
              )}

              {/* Daftar akun terjangkau */}
              <div className="space-y-2" data-testid="aks-daftar-utama">
                {utama.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Tidak ada akun yang cocok.</p>
                ) : utama.map((a) => (
                  <BarisAkun key={a.id} akun={a} nilaiDraft={a.terkunci ? null : (draft[a.id] || "ikut")}
                    hasilDraf={hasilDraf[a.id]} pratinjauLoading={pratinjauLoading}
                    onPilih={(v) => pilihTingkat(a.id, v)} />
                ))}
              </div>

              {(data.tidak_berlaku || []).length > 0 && (
                <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3"
                  data-testid="aks-tidak-berlaku">
                  <p className="font-semibold text-slate-600 mb-1">Pengecualian yang tidak berlaku lagi
                    (dibuang saat Anda menyimpan):</p>
                  {data.tidak_berlaku.map((t) => (
                    <p key={t.user_id}>{t.user_id} — {t.sebab === "akun_terhapus" ? "akun sudah dihapus" : "akun sudah jadi pemilik (superadmin)"}</p>
                  ))}
                </div>
              )}

              {/* Akun tak terjangkau, dilipat */}
              {lain.length > 0 && (
                <div>
                  <button type="button" onClick={() => setShowLain((v) => !v)} data-testid="aks-lain-toggle"
                    className="w-full flex items-center justify-between text-sm font-medium text-slate-600 py-2 border-t border-slate-100">
                    <span>Akun lain, tidak terjangkau fitur ini ({lain.length})</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showLain ? "rotate-180" : ""}`} />
                  </button>
                  {showLain && (
                    <div className="space-y-2" data-testid="aks-daftar-lain">
                      {lain.map((a) => (
                        <BarisAkun key={a.id} akun={a} nilaiDraft={a.terkunci ? null : (draft[a.id] || "ikut")}
                          hasilDraf={hasilDraf[a.id]} pratinjauLoading={pratinjauLoading}
                          onPilih={(v) => pilihTingkat(a.id, v)} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Riwayat */}
              <div className="border-t border-slate-100 pt-3" data-testid="aks-riwayat">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                  <History className="w-4 h-4" /> Riwayat perubahan
                </p>
                {riwayat.length === 0 ? (
                  <p className="text-xs text-slate-400">Belum ada perubahan tercatat.</p>
                ) : (
                  <div className="space-y-2">
                    {riwayat.map((r, i) => (
                      <div key={i} className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-2.5"
                        data-testid={`aks-riwayat-baris-${i}`}>
                        <p className="text-slate-600">
                          <b>v{r.versi}</b> · {r.pada} · oleh {r.oleh_nama || r.oleh_id} ·
                          {" "}{r.jumlah_pengecualian} akun diatur khusus
                        </p>
                        {(r.perubahan || []).length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {r.perubahan.map((p, j) => (
                              <li key={j} className="text-slate-500">
                                {p.nama || p.email || p.user_id}: {TINGKAT_LABEL[p.dari] || p.dari || "ikut dasar"}
                                {" → "}{TINGKAT_LABEL[p.ke] || p.ke || "ikut dasar"}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!riwayatHabis && (
                  <button type="button" onClick={muatRiwayatLebih} disabled={riwayatLoading}
                    data-testid="aks-riwayat-lebih"
                    className="mt-2 text-xs text-emerald-700 hover:underline disabled:opacity-50">
                    {riwayatLoading ? "Memuat…" : "Muat lebih lama"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {data && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-between gap-2 shrink-0">
            <button type="button" onClick={() => setKonfirmasiKembalikan(true)}
              disabled={busy || akunYangDiatur === 0} data-testid="aks-kembalikan"
              className="h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1.5">
              <RotateCcw className="w-4 h-4" /> Kembalikan semua ke aturan dasar
            </button>
            <button type="button" onClick={bukaKonfirmasiSimpan}
              disabled={busy || !adaPerubahan || pratinjauLoading} data-testid="aks-simpan"
              className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
              {pratinjauLoading && adaPerubahan ? "Menghitung dampak…" : "Simpan"}
            </button>
          </div>
        )}

        {guard.confirming && (
          <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} />
        )}
      </div>

      {/* Konfirmasi Simpan */}
      {konfirmasiSimpan && (
        <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4"
          onClick={() => setKonfirmasiSimpan(false)} data-testid="aks-konfirmasi">
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-sm w-full shadow-2xl p-5 space-y-3">
            <h3 className="font-display font-bold text-base text-slate-900">Simpan perubahan akses modal?</h3>
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-52 overflow-y-auto space-y-1">
              <RingkasanPerubahan perubahan={pratinjau?.perubahan} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setKonfirmasiSimpan(false)} disabled={busy}
                data-testid="aks-batal"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm disabled:opacity-50">
                Batal
              </button>
              <button type="button" onClick={simpan} disabled={busy} data-testid="aks-konfirmasi-ya"
                className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan…" : "Ya, Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Konfirmasi Kembalikan Semua */}
      {konfirmasiKembalikan && (
        <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4"
          onClick={() => setKonfirmasiKembalikan(false)} data-testid="aks-konfirmasi-kembalikan">
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-sm w-full shadow-2xl p-5 space-y-3">
            <h3 className="font-display font-bold text-base text-slate-900">
              Kembalikan SEMUA akun ke aturan dasar?
            </h3>
            <p className="text-xs text-slate-600">
              {akunYangDiatur} akun yang saat ini diatur khusus akan kembali mengikuti aturan dasar
              (izin Laporan Stok).
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setKonfirmasiKembalikan(false)} disabled={busy}
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm disabled:opacity-50">
                Batal
              </button>
              <button type="button" onClick={kembalikanSemua} disabled={busy}
                data-testid="aks-kembalikan-ya"
                className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Memproses…" : "Ya, Kembalikan Semua"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RingkasanPerubahan({ perubahan }) {
  if (!perubahan) return <p>Menghitung dampak…</p>;
  const kelompok = [
    { k: "mulai_lengkap", l: "Mulai melihat modal & margin" },
    { k: "berhenti_lengkap", l: "Berhenti melihat modal & margin" },
    { k: "mulai_kelas", l: "Mulai melihat kelas margin" },
    { k: "berhenti_kelas", l: "Berhenti melihat kelas margin" },
    { k: "menjadi_tidak", l: "Menjadi tidak melihat sama sekali" },
  ];
  const ada = kelompok.filter((g) => (perubahan[g.k] || []).length > 0);
  if (ada.length === 0) return <p>Tidak ada perubahan hasil efektif untuk akun mana pun.</p>;
  return ada.map((g) => (
    <p key={g.k}>
      <b>{g.l}:</b> {perubahan[g.k].map((x) => x.nama).join(", ")}
    </p>
  ));
}

function BarisAkun({ akun: a, nilaiDraft, hasilDraf, pratinjauLoading, onPilih }) {
  const hasilTingkat = hasilDraf || a.hasil.tingkat;
  return (
    <div className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`aks-baris-${a.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-slate-900">{a.nama}
            {a.karyawan_nonaktif && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">karyawan nonaktif</span>
            )}
            {!a.tertaut_karyawan && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">tidak tertaut karyawan</span>
            )}
            {a.berubah_sejak_dipilih && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700"
                data-testid={`aks-berubah-${a.id}`}>role/template berubah sejak dipilih</span>
            )}
          </p>
          <p className="text-xs text-slate-500">{a.email} · {a.role}{a.template_key ? ` · ${a.template_key}` : ""}</p>
          <p className="text-xs text-slate-500">{[a.jabatan, a.cabang].filter(Boolean).join(" · ") || "—"}</p>
        </div>
        <div className="flex flex-wrap gap-1 justify-end">
          {JANGKAUAN_LABEL.filter((j) => a.jangkauan?.[j.k]).map((j) => (
            <span key={j.k} className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
              {j.l}
            </span>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-500">
        Aturan dasar: {a.dasar.sebab === "izin_laporan_stok" ? "punya izin Laporan Stok (melihat modal)" : "tanpa izin Laporan Stok (tidak melihat modal)"}
        {a.dasar.berjangka_sampai && ` (izin sementara s/d ${a.dasar.berjangka_sampai})`}
      </p>

      {a.terkunci ? (
        <p className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1.5 inline-block">
          Pemilik — selalu melihat semuanya
        </p>
      ) : (
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs" role="radiogroup"
          aria-label={`Tingkat akses modal untuk ${a.nama}`}>
          {SEGMEN.map((s) => (
            <button key={s.v} type="button" role="radio" aria-checked={nilaiDraft === s.v}
              onClick={() => onPilih(s.v)}
              data-testid={`aks-pilih-${a.id}-${s.v}`}
              className={`px-2.5 h-10 min-w-[40px] font-medium ${nilaiDraft === s.v ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {s.l}
            </button>
          ))}
        </div>
      )}

      {!a.terkunci && (
        <p className="text-xs text-slate-700" data-testid={`aks-hasil-${a.id}`}>
          Hasil: <b>{HASIL_TEKS[hasilTingkat] || hasilTingkat}</b>
          {pratinjauLoading && <span className="text-slate-400"> (menghitung ulang…)</span>}
        </p>
      )}

      {!a.terkunci && hasilTingkat !== "lengkap" && (a.masih_lihat_modal_di || []).length > 0 && (
        <p className="text-[11px] text-slate-500" data-testid={`aks-masih-${a.id}`}>
          Masih melihat modal di: {a.masih_lihat_modal_di.join("; ")}
        </p>
      )}

      {!a.terkunci && (a.bisa_ditebak_lewat || []).length > 0 && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1" data-testid={`aks-tebak-${a.id}`}>
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          Bisa ditebak lewat: {a.bisa_ditebak_lewat.join("; ")}
          {hasilTingkat === "kelas" && " — pertimbangkan \"Tidak\""}
        </p>
      )}
    </div>
  );
}
